import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import SeoHead from "@/components/public/SeoHead";
import FadeIn from "@/components/public/FadeIn";
import CTASection from "@/components/public/CTASection";
import ServiceCard from "@/components/public/ServiceCard";
import GoogleReviewCard from "@/components/public/GoogleReviewCard";
import PriceCalculator from "@/components/public/PriceCalculator";
import DevisForm from "@/components/public/DevisForm";
import { Shield, Bug, TrendingDown, Droplets, Clock, Eye, Award, MapPin, ArrowRight, Star } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO_IMG = "/images/hero-accueil.jpg";

export default function HomePage() {
  const [services, setServices] = useState([]);
  const [avis, setAvis] = useState([]);
  const [villes, setVilles] = useState([]);
  const [devisService, setDevisService] = useState("");
  const [devisMessage, setDevisMessage] = useState("");

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/services`),
      axios.get(`${API}/avis`),
      axios.get(`${API}/villes`),
    ]).then(([s, a, v]) => {
      setServices(s.data);
      setAvis(a.data);
      setVilles(v.data);
    });
  }, []);

  const b2cServices = services.filter(s => s.type_client === "B2C" || s.type_client === "mixte");
  const b2bServices = services.filter(s => s.type_client === "B2B" || s.type_client === "mixte");

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Nettoyage Expert",
    "url": "https://www.nettoyage-expert.ch",
    "description": "Entreprise de nettoyage professionnel en Suisse romande. Notée 5 étoiles sur Google.",
    "inLanguage": "fr"
  };

  return (
    <div data-testid="home-page">
      <SeoHead
        title="Nettoyage professionnel Suisse romande"
        description="Nettoyage professionnel en Suisse romande : canapés, terrasses, façades dès 125 CHF. Résultat immédiat. 5★ Google. Devis gratuit en 2h. 024 539 10 33"
        path="/"
        breadcrumbs={[{ name: "Accueil" }]}
        schemas={[websiteSchema]}
      />
      {/* HERO */}
      <section className="relative min-h-[85vh] sm:min-h-[80vh] flex items-center" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img src={HERO_IMG} alt="Intérieur de salon propre après nettoyage professionnel en Suisse romande" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#062133]/90 via-[#062133]/70 to-[#062133]/50" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-full px-4 py-1.5 mb-6">
              <span className="text-amber-400 text-sm">&#9733; 5&#9733;</span>
              <span className="text-white/90 text-sm font-medium">+500 interventions &bull; Suisse romande</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-white tracking-tight leading-[1.15]">
              Entreprise de nettoyage en Suisse romande – canapés, terrasses, façades
            </h1>
            <p className="mt-5 text-lg sm:text-xl text-white/80 leading-relaxed max-w-xl">
              Nettoyage professionnel en Suisse romande. Résultat visible immédiatement.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 bg-[#336DBF] text-white font-semibold rounded-md hover:bg-[#2A5A9E] transition-colors text-base sm:text-lg" data-testid="hero-cta">
                Devis gratuit en 2 minutes
              </Link>
              <a href="tel:+41245391033" className="inline-flex items-center justify-center px-6 py-4 border border-white/30 text-white font-medium rounded-md hover:bg-white/10 transition-colors text-sm">
                024 539 10 33
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* DOULEUR */}
      <section className="py-16 sm:py-24 bg-white" data-testid="douleur-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#336DBF] mb-3">Le problème</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#062133] tracking-tight">
              Ce que vous ne voyez pas vous coûte cher
            </h2>
          </FadeIn>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-10">
            {[
              { icon: Bug, title: "Acariens et allergènes", desc: "Vos canapés et matelas abritent des millions d'acariens invisibles qui causent allergies et problèmes respiratoires." },
              { icon: Droplets, title: "Moisissures", desc: "L'humidité favorise le développement de moisissures sur les façades, toitures et surfaces intérieures." },
              { icon: TrendingDown, title: "Dégradation progressive", desc: "Sans entretien professionnel, vos surfaces se détériorent chaque jour. Les dommages deviennent irréversibles." },
              { icon: Shield, title: "Perte de valeur", desc: "Un bien mal entretenu perd de sa valeur immobilière. Terrasses, façades, toitures : chaque surface compte." },
            ].map((item, i) => (
              <FadeIn key={i} delay={i * 100}>
                <div className="p-6 border border-[#062133]/10 rounded-lg">
                  <div className="w-10 h-10 rounded-md bg-[#336DBF]/10 flex items-center justify-center mb-4">
                    <item.icon className="w-5 h-5 text-[#336DBF]" />
                  </div>
                  <h3 className="font-semibold text-[#062133] mb-2">{item.title}</h3>
                  <p className="text-sm text-[#062133]/60 leading-relaxed">{item.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* AGITATION */}
      <section className="py-16 sm:py-24 bg-[#F4F6F8]" data-testid="agitation-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="max-w-3xl">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#336DBF] mb-3">L'urgence</p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#062133] tracking-tight">
                Chaque jour qui passe aggrave le problème
              </h2>
              <p className="mt-4 text-[#062133]/60 text-lg leading-relaxed">
                Un matelas non nettoyé double de poids en 10 ans à cause des acariens. Une terrasse couverte de mousse devient glissante et dangereuse. Une façade négligée fait chuter la valeur de votre bien de 5 à 15%.
              </p>
              <p className="mt-3 text-[#062133] font-medium text-lg">
                Vos enfants jouent sur ces surfaces. Vos clients visitent ces locaux. Vos locataires vivent dans ces espaces.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* SOLUTION */}
      <section className="py-16 sm:py-24 bg-white" data-testid="solution-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#336DBF] mb-3">La solution</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#062133] tracking-tight">
              Nettoyage Expert : le résultat que vous attendez
            </h2>
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-10">
            {[
              { icon: Clock, title: "Intervention rapide", desc: "Disponibilité sous 48h. Devis en 2 heures. Équipe ponctuelle et professionnelle." },
              { icon: Eye, title: "Résultat visible", desc: "Vous voyez la différence immédiatement. Avant/après spectaculaire sur chaque intervention." },
              { icon: Award, title: "Garanti professionnel", desc: "Matériel de dernière génération, produits écologiques, satisfaction garantie ou on revient." },
            ].map((item, i) => (
              <FadeIn key={i} delay={i * 150}>
                <div className="text-center p-8 border border-[#062133]/10 rounded-lg">
                  <div className="w-14 h-14 rounded-full bg-[#336DBF]/10 flex items-center justify-center mx-auto mb-5">
                    <item.icon className="w-7 h-7 text-[#336DBF]" />
                  </div>
                  <h3 className="font-semibold text-lg text-[#062133] mb-2">{item.title}</h3>
                  <p className="text-sm text-[#062133]/60 leading-relaxed">{item.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTASection title="Prêt à voir la différence ?" subtitle="Résultat visible dès la première intervention. Devis gratuit et sans engagement." />

      {/* SERVICES */}
      <section className="py-16 sm:py-24 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#336DBF] mb-3">Nos services</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#062133] tracking-tight">
              Des solutions pour chaque besoin
            </h2>
          </FadeIn>

          <div className="mt-10">
            <h3 className="text-lg font-semibold text-[#062133] mb-4">Pour les particuliers</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {b2cServices.map(s => <ServiceCard key={s.slug} service={s} />)}
            </div>
          </div>

          <div className="mt-12">
            <h3 className="text-lg font-semibold text-[#062133] mb-4">Pour les entreprises</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {b2bServices.filter(s => s.type_client === "B2B").map(s => <ServiceCard key={s.slug} service={s} />)}
            </div>
          </div>

          <div className="mt-8 text-center">
            <Link to="/services" className="inline-flex items-center gap-2 text-[#336DBF] font-semibold hover:underline" data-testid="all-services-link">
              Voir tous nos services <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* PRICE CALCULATOR */}
      <section className="py-16 sm:py-24 bg-[#F4F6F8]" data-testid="calculator-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <PriceCalculator onRequestDevis={(serviceName, message) => {
              setDevisService(serviceName || "");
              setDevisMessage(message || "");
              setTimeout(() => {
                const el = document.getElementById("devis-section");
                if (el) el.scrollIntoView({ behavior: "smooth" });
              }, 100);
            }} />
          </FadeIn>
        </div>
      </section>

      {/* CTA */}
      <CTASection title="Plus de 500 interventions réalisées" subtitle="Rejoignez nos clients satisfaits en Suisse romande" buttonText="Obtenir mon devis gratuit" />

      {/* REVIEWS */}
      <section className="py-16 sm:py-24 bg-white" data-testid="reviews-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#336DBF] mb-3">Témoignages</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#062133] tracking-tight">
              Ce que disent nos clients
            </h2>
            <div className="mt-3 flex items-center justify-center gap-2">
              <div className="flex gap-0.5">{Array.from({length: 5}, (_, i) => <Star key={i} size={18} className="fill-amber-400 text-amber-400" />)}</div>
              <span className="text-[#062133]/60 text-lg">5.0/5 — Avis vérifiés Google</span>
            </div>
          </FadeIn>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
            {avis.slice(0, 6).map((a, i) => (
              <FadeIn key={a.id || i} delay={i * 80}>
                <GoogleReviewCard avis={a} index={i} />
              </FadeIn>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link to="/avis-clients" className="inline-flex items-center gap-2 text-[#336DBF] font-semibold hover:underline">
              Voir tous les avis <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* VILLES */}
      <section className="py-16 sm:py-24 bg-[#F4F6F8]" data-testid="villes-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#336DBF] mb-3">Zones d'intervention</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#062133] tracking-tight">
              Nous intervenons dans toute la Suisse romande
            </h2>
          </FadeIn>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-10">
            {villes.map((v, i) => (
              <FadeIn key={v.slug} delay={i * 80}>
                <Link to={`/nettoyage-canape-${v.slug}`} className="block bg-white border border-[#062133]/10 rounded-lg p-5 hover:border-[#336DBF]/30 hover:shadow-sm transition-all">
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin size={16} className="text-[#336DBF]" />
                    <h3 className="font-semibold text-[#062133]">{v.name}</h3>
                  </div>
                  <p className="text-xs text-[#062133]/60">{v.canton}</p>
                  <p className="text-xs text-[#062133]/60 mt-1">{v.quartiers?.join(", ")}</p>
                </Link>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* DEVIS FORM */}
      <section className="py-16 sm:py-24 bg-white" id="devis-section" data-testid="devis-form-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-8">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#336DBF] mb-3">Contact</p>
              <h2 className="text-2xl sm:text-3xl font-semibold text-[#062133] tracking-tight">
                Demandez votre devis gratuit
              </h2>
              <p className="mt-2 text-[#062133]/60">Réponse garantie sous 2 heures</p>
            </div>
            <div className="bg-white border border-[#062133]/10 rounded-lg p-6 sm:p-8 shadow-sm">
              <DevisForm source="direct" prefilledService={devisService} prefilledMessage={devisMessage} />
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
